
import { useState, useRef, useEffect } from "react";
import { X, Mic, Minimize2 } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { TypingIndicator } from "./TypingIndicator";
import { useIsMobile } from "@/hooks/use-mobile";
import { VoiceUI } from "./VoiceUI";
import { useWebSocketChat } from "@/hooks/chat/useWebSocketChat";
import { ChatConnectionStatus } from "./ChatConnectionStatus";

interface ChatInterfaceProps {
  onClose: () => void;
}

export function ChatInterface({ onClose }: ChatInterfaceProps) {
  const isMobile = useIsMobile();
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Use real WebSocket chat with default agent from API
  const {
    messages,
    isLoading,
    isConnected,
    sendMessage,
  } = useWebSocketChat(); // Remove hardcoded initialAgentId

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSendMessage = (content: string) => {
    sendMessage(content);
  };

  const toggleVoiceMode = () => {
    setIsVoiceMode(prev => !prev);
  };

  const toggleMinimize = () => {
    setIsMinimized(prev => !prev);
  };

  if (isMinimized) {
    return (
      <div className={`
        ${isMobile 
          ? 'fixed bottom-6 right-6 z-50' 
          : 'fixed bottom-6 right-6 z-50'
        }
      `}>
        <div 
          onClick={toggleMinimize}
          className="h-12 w-12 bg-primary rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:scale-105 transition-transform"
        >
          <ChatConnectionStatus isConnected={isConnected} />
        </div>
      </div>
    );
  }

  return (
    <div className={`
      flex flex-col relative
      ${isMobile 
        ? 'fixed inset-0 z-50 w-full h-full bg-background' 
        : 'h-[600px] w-[420px] border rounded-xl shadow-xl bg-background'
      } 
      overflow-hidden
    `}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/30 shrink-0">
        <div className="flex items-center gap-3">
          <ChatConnectionStatus isConnected={isConnected} />
          <div>
            <h3 className="font-semibold text-sm">AI Agent</h3>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={toggleVoiceMode}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Mic className="h-4 w-4" />
          </button>
          <button
            onClick={toggleMinimize}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button 
            onClick={onClose} 
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {isVoiceMode ? (
        <VoiceUI onClose={() => setIsVoiceMode(false)} />
      ) : (
        <>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1 min-h-0">
            {!isConnected && (
              <div className="text-center text-muted-foreground py-8">
                <div className="animate-pulse">
                  <p>Connecting to agent...</p>
                </div>
              </div>
            )}
            {isConnected && messages.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <p>👋 Ready to chat! Send a message to get started.</p>
              </div>
            )}
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input Area - Fixed at bottom */}
          <div className="shrink-0 border-t-0">
            <ChatInput 
              onSendMessage={handleSendMessage} 
              isLoading={isLoading}
              disabled={!isConnected}
            />
          </div>
        </>
      )}
    </div>
  );
}
